import React, { useEffect, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { UserRole } from '../globalContant';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Flex, Text } from '@adobe/react-spectrum';
import AttendanceProvider from '../Component/Academics/Attendence/Provider';
import ManageEmployeeProvider from '../Component/ManageEmployee/Provider/manageEmployeeProvider';
import ManageStudentProvider from '../Component/ManageStudent/Provider/manageStudentProvider';
import AcademicScheduleProvider from '../Component/Academics/TimeTable/Provider/academicScheduleProvider';
import ManageResultProvider from '../Component/Academics/Result/Provider/manageResultProvider';
import NoticeProvider from '../Component/Communications/Notice/Provider/noticeProvider';
import AnnouncementProvider from '../Component/Communications/Announcement/Provider/announcementProvider';
import CircularProvider from '../Component/Communications/Circular/Provider/circularProvider';
import DairiesProvider from '../Component/Communications/DiariesAndHomework/Provider/diariesProvider';
import NotesProvider from '../Component/Communications/Notes/Provider/notesProvider';
import EventProvider from '../Component/Happenings/SchoolEvents/Provider/schoolEventProvider';
import TripProvider from '../Component/Happenings/SchoolTrips/Provider/schoolTripProvider';
import WorkshopProvider from '../Component/Happenings/WorkShops/Provider/schoolWorkshopProvider';
import InstituteProfileProvier from '../Component/Profile/Providers/instituteProfileProvider';
import AccessControlProvider from '../Component/AccessControl/Provider/accessControlProvider';
import { useAppProvider } from '../applicationProvider';
import Loader from '../CommonComponent/Loader';
import TuitionFeesContainer from '../Component/Payments/TuitionFees/Container/tuitionFeesContainer';

// Lazy-loaded routes
const Dashboard = lazy(() => import('../Component/Dashboard'));
const TimeTable = lazy(() => import('../Component/Academics/TimeTable'));
const Attendence = lazy(() => import('../Component/Academics/Attendence'));
const Profile = lazy(() => import('../Component/Profile'));
const HomePage = lazy(() => import('../Component/HomePage'));
const ManageResult = lazy(() => import('../Component/Academics/Result'));
const EmployeeContainer = lazy(() => import('../Component/ManageEmployee/EmployeeContainer'));
const StudentContainer = lazy(() => import('../Component/ManageStudent/StudentContainer'));
const NoticeContainer = lazy(() => import('../Component/Communications/Notice/Container/noticeContainer'));
const AnnouncementContainer = lazy(() => import('../Component/Communications/Announcement/Container/announcementContainer'));
const CircularContainer = lazy(() => import('../Component/Communications/Circular/Container/circularContainer'));
const DairiesContainer = lazy(() => import('../Component/Communications/DiariesAndHomework/Container/diariesContainer'));
const Notes = lazy(() => import('../Component/Communications/Notes'));
const SchoolEvents = lazy(() => import('../Component/Happenings/SchoolEvents'));
const SchoolTrips = lazy(() => import('../Component/Happenings/SchoolTrips'));
const WorkShops = lazy(() => import('../Component/Happenings/WorkShops'));
const AccessControl = lazy(() => import('../Component/AccessControl'));

// Error fallback for lazy chunk load failure
function ErrorFallback({ error }: { error: Error }) {
  if (error.name === 'ChunkLoadError') {
    window.location.reload();
  }

  return (
    <Flex justifyContent="center" alignItems="center" height="100vh" direction={'column'}>
      <Text>Something went wrong</Text>
      <Button variant="cta" onPress={() => window.location.reload()}>
        Reload
      </Button>
    </Flex>
  );
}

// Logout Redirect Helper
const Redirect: React.FC<{ to: string }> = ({ to }) => {
  const navigate = useNavigate();
  const { setLoggedIn } = useAppProvider();

  useEffect(() => {
    if (to === '/') {
      setLoggedIn(false);
      Cookies.remove('loggedin');
      Object.keys(Cookies.get()).forEach((cookie) => Cookies.remove(cookie));
      sessionStorage.clear();
    }
    navigate(to);
    // eslint-disable-next-line
  }, [navigate, to]);
  return null;
};

const AuthenticatedRoutes: React.FC = () => (
  <Routes>
    <Route
      path="/profile"
      element={
        <InstituteProfileProvier>
          <Profile />
        </InstituteProfileProvier>
      }
    />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route
      path="/academic_schedule"
      element={
        <AcademicScheduleProvider>
          <TimeTable />
        </AcademicScheduleProvider>
      }
    />
    <Route
      path="/results"
      element={
        <ManageResultProvider>
          <ManageResult />
        </ManageResultProvider>
      }
    />
    <Route
      path="/attendance"
      element={
        <AttendanceProvider>
          <Attendence />
        </AttendanceProvider>
      }
    />
    <Route
      path="/manage-employee/*"
      element={
        <ManageEmployeeProvider>
          <EmployeeContainer />
        </ManageEmployeeProvider>
      }
    />
    <Route
      path="/manage-student/*"
      element={
        <ManageStudentProvider>
          <StudentContainer />
        </ManageStudentProvider>
      }
    />
    <Route
      path="/access-control/*"
      element={
        <AccessControlProvider>
          <AccessControl />
        </AccessControlProvider>
      }
    />
    <Route
      path="/notice/*"
      element={
        <NoticeProvider>
          <NoticeContainer />
        </NoticeProvider>
      }
    />
    <Route
      path="/announcements/*"
      element={
        <AnnouncementProvider>
          <AnnouncementContainer />
        </AnnouncementProvider>
      }
    />
    <Route
      path="/circular/*"
      element={
        <CircularProvider>
          <CircularContainer />
        </CircularProvider>
      }
    />
    <Route
      path="/diaries_homework/*"
      element={
        <DairiesProvider>
          <DairiesContainer />
        </DairiesProvider>
      }
    />
    <Route
      path="/notes"
      element={
        <NotesProvider>
          <Notes />
        </NotesProvider>
      }
    />
    <Route
      path="/events"
      element={
        <EventProvider>
          <SchoolEvents />
        </EventProvider>
      }
    />
    <Route
      path="/trips"
      element={
        <TripProvider>
          <SchoolTrips />
        </TripProvider>
      }
    />
    <Route
      path="/workshops"
      element={
        <WorkshopProvider>
          <WorkShops />
        </WorkshopProvider>
      }
    />
    <Route path="/tuitionFees/*" element={<TuitionFeesContainer />} />
  </Routes>
);

const MainContainer: React.FC = () => {
  const location = useLocation();
  const loggedIn = Cookies.get('loggedin');
  const userType = Cookies.get('userType');

  const protectedRoutes = useMemo(() => {
    const base = [
      '/profile',
      '/dashboard',
      '/academic_schedule',
      '/results',
      '/attendance',
      '/announcements',
      '/circular',
      '/diaries_homework',
      '/notes',
      '/events',
      '/trips',
      '/workshops',
      '/notice',
      '/tuitionFees',
    ];

    const wildcards = base.map((r) => `${r}/*`);

    if (userType === UserRole.ADMIN) {
      return [...base, '/manage-employee', '/manage-employee/*', '/manage-student', '/manage-student/*', '/access-control', '/access-control/*', ...wildcards];
    }

    return [...base, ...wildcards];
  }, [userType]);

  const isProtectedRoute = useMemo(() => protectedRoutes.some((r) => location.pathname.includes(r)), [protectedRoutes, location.pathname]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense
        fallback={
          <Flex justifyContent={'center'} alignItems={'center'} height={'100vh'}>
            <Loader />
          </Flex>
        }
      >
        <Routes>
          <Route path="/" element={loggedIn ? <Redirect to="/dashboard" /> : <HomePage />} />
        </Routes>

        {loggedIn ? isProtectedRoute ? <AuthenticatedRoutes /> : <Redirect to="/dashboard" /> : <Redirect to="/" />}
      </Suspense>
    </ErrorBoundary>
  );
};

export default MainContainer;
